import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { NeptuneApi } from '../genericApi/neptune.api';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import type { MfaVerifyModel } from '../.././Core/Users/models/mfa.model';
import { LoginMfaValidationRequiredResponse, LoginResponse, LoginSSORedirectResponse, LoginSuccessResponse } from './login-response';

export interface SsoState {
    username: string;
    nonce: string;
    createDate: Date;
    deletedDate: Date;
}

@Injectable()
export class LoginApi {
    constructor(
        private neptuneApi: NeptuneApi,
    ) {}

    performLogin(username: string, password: string, force: boolean = false): Observable<LoginResponse> {

        let headers = new HttpHeaders();

        headers = headers.set('Authorization', 'Basic ' + btoa(username + ':' + password));

        let payload = null;

        if (force) {
            payload = {};

            if (force) {
                payload.force = force;
            }
        }

        return this.neptuneApi.api<LoginResponse>(
            '/login',
            payload,
            HttpVerbsEnum.POST,
            null,
            null,
            headers
        );
    }

    performMfaLogin(
        username: string,
        password: string,
        force: boolean = false,
        mfaCredentials?: MfaVerifyModel
    ): Observable<LoginMfaValidationRequiredResponse> {

        let headers = new HttpHeaders();

        headers = headers.set('Authorization', 'Basic ' + btoa(username + ':' + password));

        let payload = null;

        if (force || mfaCredentials) {
            payload = {};

            if (force) {
                payload.force = force;
            }

            if (mfaCredentials) {
                payload.mfaVerification = {
                    methodId: mfaCredentials.methodId,
                    name: mfaCredentials.name,
                    verificationCode: mfaCredentials.verificationCode
                };
            }
        }

        return this.neptuneApi.api<LoginMfaValidationRequiredResponse>(
            '/login',
            payload,
            HttpVerbsEnum.POST,
            null,
            null,
            headers
        );
    }

    performSsoLogin(username: string): Observable<LoginSSORedirectResponse> {
        let headers = new HttpHeaders();

        // No password needed
        headers = headers.set('Authorization', 'Basic ' + btoa(username + ':'));

        return this.neptuneApi.api<LoginSSORedirectResponse>(
            '/login',
            // TODO: Need force?
            null,
            HttpVerbsEnum.POST,
            null,
            null,
            headers
        );
    }

    performSsoCodeExchange(code: string, state: SsoState): Observable<LoginResponse> {
        return this.neptuneApi.api<LoginResponse>(
            '/oauth2/code',
            null,
            HttpVerbsEnum.GET,
            {
                code: code,
                state: state
            },
            null
        );
    }

    performLogout(reason: string): Observable<void> {
        return this.neptuneApi.api<void>(
            `/logout?reason=${reason}`,
            null,
            HttpVerbsEnum.DELETE,
        );
    }
    /**
     * username (string)
     * @user returns user information with only providing the username
     */
    getUserByUsername(user: { username: string }): Observable<{neptune: boolean; userId: number; accountId: number}> {
        return this.neptuneApi.api<{neptune: boolean; userId: number; accountId: number}>(
            '/login/ghostin/usersearch',
            user,
            HttpVerbsEnum.POST,
        );
    }

    /**
     * Accepts a userId (number) or username (string)
     * @user string value that will be casted if number
     */
    ghostIn(user: { userId: number } | { username: string }): Observable<{token: string}> {
        return this.neptuneApi.api<{token: string}>(
            '/login/ghostin',
            user,
            HttpVerbsEnum.POST,
        );
    }

    ghostOut(): Observable<any> {
        return this.neptuneApi.api<any>(
            '/login/ghostin',
            null,
            HttpVerbsEnum.DELETE,
        );
    }

    switchAccount(id: number): Observable<LoginSuccessResponse> {
        return this.neptuneApi.api<LoginSuccessResponse>(
            '/login/switchaccount',
            {
                accountId: id
            },
            HttpVerbsEnum.POST,
        );
    }

    changePassword(oldPass: string, newPass: string): Observable<void> {
        return this.neptuneApi.api<void>(
            '/login/credentials',
            {
                password: newPass,
                oldPassword: oldPass
            },
            HttpVerbsEnum.PUT,
        );
    }

    generateSSO(partnerId: number, expirationDate: number): Observable<LoginSuccessResponse> {
        return this.neptuneApi.api<LoginSuccessResponse>(
            `/login/sso/auths/partners/${partnerId}`,
            {
                'expirationDate': expirationDate
            },
            HttpVerbsEnum.POST
        );
    }

    getSSO(partnerId: number): Observable<LoginSuccessResponse> {
        return this.neptuneApi.api<LoginSuccessResponse>(
            `/login/sso/auths/partners/${partnerId}`,
            null,
            HttpVerbsEnum.GET
        );
    }

    deleteSSO(partnerId: number): Observable<void> {
        return this.neptuneApi.api(
            `/login/sso/auths/partners/${partnerId}`,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    sendMfaEmailConfirmation(email: string, id: number, userName: string): Observable<void> {
        const request = {
            email: email,
            methodId: id,
            username: userName

        };
        return this.neptuneApi.api(
            '/login/mfa/sendverification',
            request,
            HttpVerbsEnum.POST
        );
    }
}
